import { useState, useEffect } from 'react';

/** First column: top pixel and bottom pixel as CSS rgb(r,g,b) strings. */
export interface TokenGradientColors {
  color1: string;
  color2: string;
}

/**
 * Loads token image, draws to canvas, reads first column first and last pixel.
 * Returns { color1, color2 } for vertical gradient, or null on failure.
 *
 * Returns null when:
 * - Image host does not send CORS headers (Access-Control-Allow-Origin). The browser
 *   then "taints" the canvas and getImageData() throws SecurityError. The image URL
 *   must be served with CORS allowed for pixel read to work.
 * - Image fails to load (onerror), or canvas/context unavailable.
 */
export function useTokenImageGradient(imageUrl: string | null): TokenGradientColors | null {
  const [colors, setColors] = useState<TokenGradientColors | null>(null);

  useEffect(() => {
    if (!imageUrl?.trim()) {
      setColors(null);
      return;
    }

    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      if (cancelled) return;
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          setColors(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        const w = canvas.width;
        const h = canvas.height;
        if (w === 0 || h === 0) {
          setColors(null);
          return;
        }
        const imageData = ctx.getImageData(0, 0, w, h);
        const data = imageData.data;
        const getRgb = (x: number, y: number) => {
          const i = (y * w + x) * 4;
          const r = data[i] ?? 0;
          const g = data[i + 1] ?? 0;
          const b = data[i + 2] ?? 0;
          return `rgb(${r},${g},${b})`;
        };
        const color1 = getRgb(0, 0);
        const color2 = getRgb(0, h - 1);
        setColors({ color1, color2 });
      } catch (err) {
        if (process.env.NODE_ENV === 'development' && err instanceof Error) {
          console.warn('[useTokenImageGradient] Pixel read failed (often CORS). Image must be served with Access-Control-Allow-Origin.', err.message);
        }
        setColors(null);
      }
    };

    img.onerror = () => {
      if (!cancelled) setColors(null);
    };

    img.src = imageUrl;

    return () => {
      cancelled = true;
      img.src = '';
    };
  }, [imageUrl]);

  return colors;
}
